import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';

import IconButton from 'components/partials/button/icon-button';
import { Select } from 'components/partials/select/select';

import Icons from 'assets/icons';

import { useComparisonRowResults } from '../hooks/use-comparison-row-results';
import { COMPARISON_METRICS } from '../results-step.constants';

import type {
  ComparisonMetric,
  VersionOption,
} from '../hooks/use-compare-estimates';

export interface CompareEstimatesTableRow {
  id: string;
  isCurrent: boolean;
  versionLabel: string;
  versionId: number | undefined;
  selectedVersionId: number | undefined;
  currentMetrics: ComparisonMetric[];
  canRemove: boolean;
}

interface GetCompareEstimatesTableSettingsArgs {
  userId: string;
  versionOptions: VersionOption[];
  selectedVersionIds: Set<number>;
  onSelectVersion: (rowId: string, versionId: number | undefined) => void;
  onRemoveRow: (rowId: string) => void;
  selectControlStyles: Record<string, unknown>;
  selectStyles: () => Record<string, unknown>;
}

type CellInfo = { row: { original: CompareEstimatesTableRow } };

interface VersionCellProps {
  row: CompareEstimatesTableRow;
  userId: string;
  versionOptions: VersionOption[];
  selectedVersionIds: Set<number>;
  onSelectVersion: (rowId: string, versionId: number | undefined) => void;
  selectControlStyles: Record<string, unknown>;
  selectStyles: () => Record<string, unknown>;
}

const VersionCell = ({
  row,
  userId,
  versionOptions,
  selectedVersionIds,
  onSelectVersion,
  selectControlStyles,
  selectStyles,
}: VersionCellProps): React.JSX.Element => {
  // Hook must run unconditionally (Rules of Hooks): for the current row we
  // pass `undefined`, which keeps the query disabled and `isLoading` unused.
  const { isLoading } = useComparisonRowResults(
    userId,
    row.isCurrent ? undefined : row.selectedVersionId
  );

  if (row.isCurrent) {
    const currentOption: VersionOption = {
      label: row.versionLabel,
      value: row.versionId ?? 0,
    };

    return (
      <Select
        options={[currentOption]}
        value={currentOption}
        disabled
        selectClass="w-full max-w-[323px]"
        extraBasicStyles={selectControlStyles}
        extraStyles={selectStyles}
        data-testid="compare-current-version-select"
      />
    );
  }

  const availableOptions = versionOptions.filter(
    (opt) =>
      opt.value === row.selectedVersionId || !selectedVersionIds.has(opt.value)
  );

  return (
    <Select
      options={availableOptions}
      value={row.selectedVersionId}
      onChange={(option) => {
        const selected = option as VersionOption | null;

        onSelectVersion(row.id, selected?.value);
      }}
      disabled={isLoading}
      placeholder="Select version..."
      aria-label="Select version to compare"
      selectClass="w-full max-w-[323px]"
      extraBasicStyles={selectControlStyles}
      menuPortalTarget={document.body}
      extraStyles={selectStyles}
      isLoading={isLoading}
      data-testid={`compare-row-${row.id}-version-select`}
    />
  );
};

interface MetricCellProps {
  row: CompareEstimatesTableRow;
  userId: string;
  metricKey: string;
  isLastMetricColumn: boolean;
  onRemoveRow: (rowId: string) => void;
}

const MetricCell = ({
  row,
  userId,
  metricKey,
  isLastMetricColumn,
  onRemoveRow,
}: MetricCellProps): React.JSX.Element => {
  const { metrics, isLoading } = useComparisonRowResults(
    userId,
    row.isCurrent ? undefined : row.selectedVersionId
  );

  const displayValue = row.isCurrent
    ? row.currentMetrics.find((m) => m.key === metricKey)?.value
    : metrics.find((m) => m.key === metricKey)?.value;

  const showSpinner = !row.isCurrent && isLoading;
  const showValue = row.isCurrent || !!row.selectedVersionId;

  return (
    <div className="flex items-center justify-between">
      <span>
        {showSpinner ? (
          <Icons.Spinner className="size-4 animate-spin text-gray-400" />
        ) : (
          showValue && displayValue
        )}
      </span>
      {isLastMetricColumn && row.canRemove && (
        <IconButton
          icon={<Icons.Close className="size-3" />}
          size="xs"
          variant="reserve-text"
          onClick={() => onRemoveRow(row.id)}
          aria-label="Remove version row"
          data-testid={`compare-row-${row.id}-remove`}
        />
      )}
    </div>
  );
};

export const getCompareEstimatesTableSettings = ({
  userId,
  versionOptions,
  selectedVersionIds,
  onSelectVersion,
  onRemoveRow,
  selectControlStyles,
  selectStyles,
}: GetCompareEstimatesTableSettingsArgs): ColumnDef<
  CompareEstimatesTableRow,
  unknown
>[] => [
  {
    id: 'version',
    header: () => <span>Version</span>,
    cell: (info: CellInfo) => (
      <VersionCell
        row={info.row.original}
        userId={userId}
        versionOptions={versionOptions}
        selectedVersionIds={selectedVersionIds}
        onSelectVersion={onSelectVersion}
        selectControlStyles={selectControlStyles}
        selectStyles={selectStyles}
      />
    ),
    headerClassName: 'rounded-tl-md rounded-bl-md',
    className: 'w-[35%] px-2.5 py-2',
    enableSorting: false,
  },
  ...COMPARISON_METRICS.map((metric, index) => {
    const isLastMetricColumn = index === COMPARISON_METRICS.length - 1;

    return {
      id: metric.key,
      header: () => <span>{metric.label}</span>,
      cell: (info: CellInfo) => (
        <MetricCell
          row={info.row.original}
          userId={userId}
          metricKey={metric.key}
          isLastMetricColumn={isLastMetricColumn}
          onRemoveRow={onRemoveRow}
        />
      ),
      headerClassName: isLastMetricColumn ? 'rounded-tr-md rounded-br-md' : '',
      className: 'w-[13%] px-2.5 py-2 text-base text-gray-700',
      enableSorting: false,
    };
  }),
];
